// ─── Inventory module — domain dictionary ────────────────────────────────────
//
// Single source of truth for the Inventory tabs' terminology (product states,
// consumable/reagent statuses, storage tiers). Centralized here so labels can
// later be swapped for i18n keys without touching the components that render
// them, and so the mock fixture and the tabs cannot drift apart.
//
// This module is UI-only: there is no backend for inventory yet, so these types
// describe the mock fixture in `@/mocks/inventory`, not an API contract.

import type { StorageCondition } from './storage'
import type { ReorderPointSource, ReorderState } from '@/lib/reorder'

// ─── Products ───────────────────────────────────────────────────────────────

/** Lifecycle state of a therapy product, in roughly chronological order. */
export type ProductState =
  | 'created'
  | 'in_mfg'
  | 'in_qc'
  | 'quarantined'
  | 'released'
  | 'reserved'
  | 'shipped'
  | 'infused'
  | 'archived'

/** Display labels for every product state (the visible text). */
export const PRODUCT_STATE_LABELS: Record<ProductState, string> = {
  created: 'Created',
  in_mfg: 'In Mfg',
  in_qc: 'In QC',
  quarantined: 'Quarantined',
  released: 'Released',
  reserved: 'Reserved',
  shipped: 'Shipped',
  infused: 'Infused',
  archived: 'Archived',
}

/**
 * A patient-specific therapy product, keyed by DIN (ISBT 128 donation
 * identification number). A product can be split into child aliquots — each
 * child keeps the parent DIN plus a suffix (`12345-001` → `12345-001-A1`), so
 * lineage is readable straight off the identifier.
 */
export type InventoryProduct = {
  id: string
  /** ISBT 128 donation identification number; child aliquots carry a suffix. */
  din: string
  productCode: string
  /** Synthetic patient / donor reference, e.g. `P-000145 / D-0098`. */
  patientDonor: string
  state: ProductState
  storageLocation: string
  /** ISO date, or null while the product has no assigned expiry (in mfg/shipped). */
  expiry: string | null
  /** Days until expiry. Stored rather than computed so the mock stays stable. */
  daysToExpiry: number | null
  /** The row action offered for this state — mock-only, does nothing. */
  action: 'move' | 'review' | 'track'
}

// ─── Consumables ────────────────────────────────────────────────────────────

/** Two-bin Kanban replenishment status for a consumable SKU. */
export type ConsumableStatus = 'ok' | 'reorder' | 'backorder'

export const CONSUMABLE_STATUS_LABELS: Record<ConsumableStatus, string> = {
  ok: 'OK',
  reorder: 'Reorder',
  backorder: 'Backorder',
}

/** A tracked consumable SKU on the replenishment worklist. */
export type Consumable = {
  id: string
  sku: string
  category: string
  onHand: number
  /** Reorder point — `onHand` at or below this fires a purchase order. */
  reorderPoint: number
  orderQty: number
  status: ConsumableStatus
  lot: string
  /** Lot expiry as `YYYY-MM` — consumables are tracked to the month. */
  lotExpiry: string
}

// ─── Reagents ───────────────────────────────────────────────────────────────

/** Release status of a reagent lot. `hold_coa` blocks consumption. */
export type ReagentStatus = 'released' | 'quarantine' | 'hold_coa'

export const REAGENT_STATUS_LABELS: Record<ReagentStatus, string> = {
  released: 'Released',
  quarantine: 'Quarantine',
  hold_coa: 'Hold (COA)',
}

/** An expiry-controlled reagent lot (GMP media, cytokines, vectors, solutions). */
export type Reagent = {
  id: string
  name: string
  grade: 'GMP' | 'USP' | 'Clinical'
  lot: string
  size: string
  /** Required storage condition, e.g. `2–8 °C`. */
  storage: string
  expiry: string
  daysToExpiry: number
  status: ReagentStatus
}

// ─── Kits ───────────────────────────────────────────────────────────────────

/** Where a kit sits on the assembly worklist. */
export type KitStatus = 'open' | 'kitting' | 'verify'

export const KIT_STATUS_LABELS: Record<KitStatus, string> = {
  open: 'Open',
  kitting: 'Kitting',
  verify: 'Verify',
}

/** A single component drawn into a kit from consumables/reagents stock. */
export type KitComponent = {
  name: string
  /** Lot or supply identifier the component was pulled from. */
  supplyId: string
  qty: number
  location: string
  /** ISO date — the kit's own expiry is the earliest of these. */
  expiry: string
  verified: boolean
}

/**
 * A pre-defined kit assembled from consumables & reagents. A kit's expiry is
 * always the earliest component expiry — derived, never stored, so it cannot
 * contradict the component list.
 */
export type Kit = {
  id: string
  kitId: string
  name: string
  /** Assembly line / suite the kit is staged for. */
  line: string
  status: KitStatus
  components: KitComponent[]
}

// ─── Storage ────────────────────────────────────────────────────────────────

/** A single addressable position within a rack. */
export type StorageSlot = {
  position: number
  /** Occupant label (e.g. `HPC(A) Bag`), or null when the slot is free. */
  label: string | null
  /** Abbreviated DIN of the occupying product, e.g. `DIN …-A1`. */
  din: string | null
}

/**
 * A node in the storage hierarchy (Category → Device → Position).
 *
 * A node either has `children`, or `slots` (a modeled rack), or an explicit
 * `used`/`capacity` leaf count. `storageUsage()` in `@/lib/inventory` resolves
 * any node to a `{ used, capacity }` pair, so parents always agree with the
 * children beneath them.
 */
export type StorageDevice = {
  id: string
  name: string
  /** Flags a device needing attention (e.g. quarantine filling up). */
  attention?: boolean
  children?: StorageDevice[]
  slots?: StorageSlot[]
  used?: number
  capacity?: number
}

/** Facility storage tiers, listed beside the dashboard's storage breakdown. */
export const STORAGE_TIERS = [
  'Vapor LN₂',
  'Ultra-low −80',
  'Refrigerated 2–8',
  'Controlled RT',
]

/** Operating note shown under the storage grid. */
export const STORAGE_FILL_NOTE =
  'Fill order: Freezer 1 first, then Freezer 2. Any location can be blocked. Scan location + product to Store / Retrieve.'

/**
 * Stand-in for a figure this mock deliberately does not invent. Every number the
 * UI shows is either derived from rows visible on the same screen, or this.
 */
export const NO_VALUE = '-:-'

/**
 * What a material may be created as.
 *
 * `Kit` is deliberately absent. A kit is assembled from other materials through
 * the Kits flow, which creates its own `Kit`-typed material with a supply id and
 * a bill of materials behind it — one made by hand here would look like a kit in
 * every list while having nothing in it. The Inventory Kits tab still filters on
 * `materialType: 'Kit'`, so kits made the proper way show up as they always did.
 */
export const CREATABLE_MATERIAL_TYPES = ['Consumable', 'Reagent'] as const

// ─── Master data (mirrors cgt-backend inventory module) ──────────────────────
//
// These types mirror the backend's materials/profiles/field-defs contract
// (Prisma enums + API row shapes) under `/inventory`. Unlike the mock types
// above, this section describes a real backend contract — keep it in sync
// manually with cgt-backend/prisma/schema.prisma when either side changes.

/** Profile category a material's fields are grouped under (mirrors the Prisma enum). */
export type InventoryProfileType =
  | 'material_description'
  | 'manufacturer'
  | 'inventory_unit'
  | 'storage'
  | 'product_code'
  | 'traceability'
  | 'shelf_life'
  | 'quality'
  | 'usage'
  | 'regulatory'
  | 'cost'
  | 'attachments'
  | 'actions'
  | 'status'

/** Profile types that can be shared/reused across multiple materials. */
export const SHARED_PROFILE_TYPES: InventoryProfileType[] = [
  'manufacturer',
  'storage',
  'traceability',
  'quality',
  'regulatory',
]

/** Lifecycle status of a material (mirrors the Prisma enum). */
export type MaterialStatus =
  | 'available'
  | 'reserved'
  | 'in_transit'
  | 'quarantine'
  | 'hold'
  | 'retired'
  /**
   * Set automatically while the connected Inventory procedure has a form in
   * progress — see `procedureId` below. Not settable by hand.
   */
  | 'in_process'

/** Primitive data type a field definition's value is validated/rendered as. */
export type FieldDataType = 'string' | 'number' | 'boolean' | 'date' | 'enum' | 'file'

/**
 * Per-dataType constraint rules carried by a field definition.
 *
 * Mirrors `FieldValidation` in `cgt-backend/src/inventory/field-defs/field-validation.ts`
 * — the CGT domain dictionary is duplicated by design, so both sides must change
 * together. Which keys are meaningful depends on the def's `dataType`; the
 * backend rejects keys that don't apply.
 */
export interface FieldValidation {
  /** Every dataType: reject null / undefined / empty-string values. */
  required?: boolean
  // number
  min?: number
  max?: number
  integer?: boolean
  step?: number
  // string
  minLength?: number
  maxLength?: number
  /** JS regular-expression source. */
  pattern?: string
  // date — ISO-8601 strings
  minDate?: string
  maxDate?: string
  // enum
  multiple?: boolean
  // file
  /** Accepted extensions ('.pdf') and/or MIME types. */
  accept?: string[]
  maxSizeMb?: number
}

/** A field definition authored for a given profile type. */
export interface ApiFieldDef {
  id: string
  profileType: InventoryProfileType
  key: string
  label: string
  description?: string | null
  dataType: FieldDataType
  order: number
  activeByDefault: boolean
  options?: string[]
  validation?: FieldValidation | null
}

/** A single field's value within a profile's field map. */
export interface ProfileFieldEntry {
  active: boolean
  value: unknown
}

/** A profile instance (e.g. one manufacturer, one storage config). */
export interface ApiProfile {
  id: string
  profileType: InventoryProfileType
  name: string
  description?: string | null
  fields: Record<string, ProfileFieldEntry>
  isDefault: boolean
}

/** Lightweight reference to a profile, embedded on a material row. */
export interface ApiProfileRef {
  id: string
  name: string
}

/** A material row as returned by the inventory materials API. */
export interface ApiMaterial {
  id: string
  materialId: string
  materialCode: string
  materialName: string
  materialType: string
  /**
   * Set only on materials that ARE a kit type, created by completing a build of
   * that kit form. Null for ordinary catalogue materials — including any
   * hand-authored "Kit" left over from before kits were produced by building.
   */
  kitFormFamilyId?: string | null
  status: MaterialStatus
  /**
   * The Inventory-type Procedure TEMPLATE gating this material — every lot
   * received against this material spawns its own run of it, and each run's
   * gate status lives on the lot (`ApiLot.gateStatus`), not here. Null when
   * unconnected. The template's own name is not embedded here — resolve
   * against the Inventory-typed rows of `procedureTemplatesApi.list()`, which
   * the Connect dialog already fetches.
   */
  procedureTemplateId?: string | null
  /** Product codes live on the material, not a shared profile. */
  primaryProductCode?: string | null
  productCodes?: string[]
  /**
   * The validated storage condition this material must be kept at (§5.2).
   * Compared against what a location provides when stock is put away; null
   * means not stated, which passes.
   */
  requiresCondition?: StorageCondition | null
  manufacturerProfile?: ApiProfileRef | null
  storageProfile?: ApiProfileRef | null
  traceabilityProfile?: ApiProfileRef | null
  qualityProfile?: ApiProfileRef | null
  regulatoryProfile?: ApiProfileRef | null
  materialDescriptionProfile?: ApiProfileRef | null
  inventoryUnitProfile?: ApiProfileRef | null
  productCodeProfile?: ApiProfileRef | null
  shelfLifeProfile?: ApiProfileRef | null
  usageProfile?: ApiProfileRef | null
  costProfile?: ApiProfileRef | null
  attachmentsProfile?: ApiProfileRef | null
  /** The material's set of row actions; null = the tenant's Default actions profile. */
  actionsProfileId?: string | null
  actionsProfile?: ApiProfileRef | null
  attributes: Record<string, unknown>
  lots?: ApiLot[]
  totalQuantity?: number
  /** Net outstanding hold. Drives `availableQuantity`; not what Reserved shows. */
  reservedQuantity?: number
  /** Spoken for and NOT yet used — the Reserved column. */
  heldQuantity?: number
  /** Actually used. Consumption already reduced the quantity, so this is history. */
  consumedQuantity?: number
  /** totalQuantity minus reservedQuantity — what can still be committed. */
  availableQuantity?: number
  /**
   * Where the material stands against its reorder point (z8nrz7f7dz). Absent
   * from a backend that predates re-ordering, so every reader tolerates that.
   */
  reorder?: MaterialReorderSummary
}

/** The re-order verdict embedded on a material row. */
export interface MaterialReorderSummary {
  state: ReorderState
  reorderPoint: number | null
  reorderPointSource: ReorderPointSource | null
  suggestedQty: number
  expired: boolean
  /** Quantity on open (DRAFT + SENT) purchase orders, when the server sends it. */
  onOrder?: number
}

export interface ApiLot {
  id: string
  /**
   * OUR id, always server-minted: `LOT-<yy>-<nnnn>-<n>`.
   *
   * The base (`nnnn`) identifies the vendor lot — every receipt of the same
   * supplier lot of this material shares it — and the suffix (`n`) counts the
   * receipt within it, from 1. Treat it as opaque: the suffix is not
   * zero-padded, so `-10` sorts below `-2` as text and these must never be
   * ordered or compared as strings.
   */
  lotNumber: string
  /**
   * The manufacturer's number printed on the container, recorded beside ours
   * rather than instead of it — a supplier recall quotes theirs, everything
   * internal refers to ours. Absent on self-produced or re-labelled stock.
   */
  vendorLotNumber?: string | null
  quantity: number
  expiryDate?: string | null
  note?: string | null
  /**
   * Everything held against this lot; not yet deducted from `quantity`.
   * The SUM of `scheduledQuantity` and `inUseQuantity` — and the only one of
   * the three `availableQuantity` is derived from.
   */
  reservedQuantity?: number
  /**
   * Spoken for and NOT yet used — the Reserved column. Read from the same open
   * holds the detail dialog lists, so the two can never disagree.
   */
  heldQuantity?: number
  /** Actually used. Consumption already reduced `quantity`, so this is history. */
  consumedQuantity?: number
  /** `quantity` minus what is held — what a new step can still commit. */
  availableQuantity?: number
  /** StorageZone this lot physically sits in; null = not yet homed. */
  zoneId?: string | null
  /** Bin / shelf code within the zone, e.g. "A1-S2-B04". */
  locationCode?: string | null
  /** Storage-hierarchy position — the truth now; supersedes zone + code. */
  storageNodeId?: string | null
  /** Materialised address of that position, e.g. "LN2-01/Vapor/R1/C1/A1". */
  storageNodePath?: string | null
  /** The run's patient, when it has one — decides which route the lot's gate badge opens. */
  procedurePatientMrn?: string | null
  /**
   * The run spawned for THIS receipt, when its material is connected to an
   * Inventory procedure template. Null if the run was since deleted, or the
   * material was never connected.
   */
  procedureId?: string | null
  /**
   * Quarantine is a fact about a RECEIPT, not the catalogue entry: one
   * delivery can clear while the next is still being inspected. Null means
   * not gated — stock received before its material was connected, or a lot of
   * an ungated material — and must not render as a status badge.
   */
  gateStatus?: 'quarantine' | 'in_process' | 'available' | null
}

/** Paginated result shape for the materials list endpoint. */
export interface MaterialListResult {
  items: ApiMaterial[]
  total: number
}

// ─── Pending inventory (I13 / I14) ────────────────────────────────────────
//
// GET /inventory/pending — one row per LOT still short of release (the gate
// moved from the material to the lot in I14: quarantine is a fact about a
// receipt, not a catalogue entry). A row disappears by itself once its lot is
// released; there is no dismiss action.

/** A lot still short of release, and the run gating it. */
export interface PendingRow {
  lotId: string
  lotNumber: string
  vendorLotNumber: string | null
  quantity: number
  expiryDate: string | null
  /** Only ever 'quarantine' or 'in_process' from this endpoint — never released. */
  gateStatus: string
  materialId: string
  materialCode: string
  materialName: string
  /** The run spawned for this receipt, or null if it was since deleted — that row is not clickable. */
  procedureId: string | null
  procedureTemplateName: string | null
  /**
   * Set when the run was raised from a patient's chart. Decides which route
   * the row opens: the patient-less page omits the MRN when starting a form,
   * which the API refuses for a procedure that has one.
   */
  patientMrn: string | null
  receivedAt: string
  completedForms: number
  totalForms: number
}

// ─── Re-order (z8nrz7f7dz) ────────────────────────────────────────────────
//
// GET /inventory/reorder — one row per material, with the figures the
// re-order decision was made from. The rule itself is in src/lib/reorder.ts.

/** Which rows `GET /inventory/reorder` returns. `attention` = reorder + out. */
export type ReorderFilter = 'attention' | 'reorder' | 'out' | 'expired' | 'all'

/** Narrows the materials list to one stock condition. */
export type MaterialStockFilter = 'low' | 'out' | 'expired'

export interface ReorderRow {
  /** The material's database id. */
  id: string
  materialId: string
  materialCode: string
  materialName: string
  materialType: string
  supplier: string | null
  onHand: number
  available: number
  /** Quantity on open (DRAFT + SENT) purchase orders. */
  onOrder: number
  /** How many open purchase orders carry this material, when the server says. */
  openPoCount?: number
  dailyConsumption: number | null
  /** `history`: not set on the material, so the server derived it from 90 days of consumption. */
  dailyConsumptionSource: 'set' | 'history' | null
  orderLeadTimeDays: number | null
  releaseTimeDays: number | null
  safetyStockDays: number | null
  totalLeadTimeDays: number | null
  safetyStockUnits: number | null
  reorderPoint: number | null
  reorderPointSource: ReorderPointSource | null
  suggestedQty: number
  state: ReorderState
  expired: boolean
  expiredQty: number
}

export interface ReorderCounts {
  attention: number
  reorder: number
  out: number
  expired: number
}

export interface ReorderListResult {
  items: ReorderRow[]
  counts: ReorderCounts
}
